import { format } from 'date-fns';
import { Clock, User, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AppointmentCardProps {
  customerName: string;
  serviceName: string;
  startTime: string;
  endTime: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'no_show' | 'completed';
  notes?: string;
  onClick?: () => void;
  isNext?: boolean;
}

export function AppointmentCard({
  customerName,
  serviceName,
  startTime,
  endTime,
  status,
  notes,
  onClick,
  isNext = false,
}: AppointmentCardProps) {
  const { t } = useTranslation(['staff', 'dashboard']);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'completed':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'no_show':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-4 rounded-lg border transition-colors',
        isNext
          ? 'bg-blue-50 border-blue-200'
          : 'bg-white border-gray-200 hover:bg-gray-50',
        onClick && 'cursor-pointer'
      )}
    >
      {isNext && (
        <div className="mb-2">
          <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-md">
            {t('staff:dashboard.todaySchedule.nextAppointment')}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate">{customerName}</h3>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <p className="text-sm text-gray-600 truncate">{serviceName}</p>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              {format(new Date(startTime), 'HH:mm')} - {format(new Date(endTime), 'HH:mm')}
            </p>
          </div>
          {notes && (
            <p className="text-xs text-gray-500 mt-2 italic line-clamp-2">{notes}</p>
          )}
        </div>
        <div className="flex-shrink-0 ml-4">
          <Badge className={cn('border', getStatusColor(status))}>
            {t(`dashboard:status.${status}`, status.replace('_', ' '))}
          </Badge>
        </div>
      </div>
    </div>
  );
}
